import { log } from '@/shared/utils/log';

import { EncryptionType, KeyPair } from '../../secure-json/core/crypto-core/types';
import { CollectionName } from '../constants';
import { CollectionsSjMap } from '../types';

import { isActualVersion } from './helpers';
import { ACTUAL_SCHEMA_VERSION, MIGRATIONS_LIST } from './migrations';
import {
  MigratedMigrationCollection,
  MigratedMigrationCollectionsData,
  MigrationError,
  MigratorError,
  OnUserActionRequiredFn,
} from './types';

export class Migrator {
  private latestMigratedData: MigratedMigrationCollectionsData = new Map();
  private collectionsData: MigratedMigrationCollectionsData = new Map();

  constructor(
    private latestMigratedVersion: number,
    private encryptionType: EncryptionType,
    private keyPair: KeyPair,
    private onUserActionRequired: OnUserActionRequiredFn,
  ) {
    log.trace('[Migrator] created');
  }

  public async migration(initialCollections: CollectionsSjMap) {
    if (isActualVersion(this.latestMigratedVersion)) {
      return {};
    }

    if (this.latestMigratedVersion > ACTUAL_SCHEMA_VERSION) {
      return {
        error: new MigratorError('Data migration not possible, you need to refresh page manually'),
      };
    }

    this.collectionsData = this.getDataForMigration(initialCollections);

    try {
      const migratedCollections = await this.migrateCollections();

      return { migratedCollections };
    } catch (er) {
      if (er instanceof MigrationError) {
        return { error: new MigratorError('migrate error', er) };
      }

      return { error: new MigratorError('unknown error') };
    }
  }

  private getDataForMigration(initialCollections: CollectionsSjMap) {
    log.trace('[Migrator] getDataForMigration');

    const collectionsForMigrations = new Map<CollectionName, MigratedMigrationCollection>();

    initialCollections.forEach((collectionData, collectionName) => {
      const collectionItems = collectionData.getDataToSerialize().items;

      if (!collectionItems.size) {
        return;
      }

      const decryptedDataMap = new Map();

      collectionItems.forEach((item, dtoId) =>
        decryptedDataMap.set(dtoId, { ...item.decryptedData }),
      );

      collectionsForMigrations.set(collectionName, decryptedDataMap);
    });

    return collectionsForMigrations;
  }

  private async migrateCollections() {
    log.trace('[Migrator] migrateCollections');

    const migrationsToApply = MIGRATIONS_LIST.filter(
      ({ version }) => version > this.latestMigratedVersion,
    );

    for (const migration of migrationsToApply) {
      if (!this.collectionsData) {
        throw new MigrationError('collection missing');
      }

      const { migrate, version } = migration;

      // Update all collections with migrated data from latestMigratedData
      this.latestMigratedData.forEach((collection, collectionName) => {
        this.collectionsData.set(collectionName, collection);
      });

      let migratedCollections;
      try {
        migratedCollections = await migrate(this.collectionsData, {
          encryptionType: this.encryptionType,
          keyPair: this.keyPair,
          hookUpdateCollections: this.onUserActionRequired,
        });
      } catch (er) {
        throw new MigrationError('error during migration', this.latestMigratedVersion);
      }

      //Update latestMigratedData from new migrated collections
      migratedCollections.forEach((collection, collectionName) => {
        this.latestMigratedData.set(collectionName, collection);
      });

      this.latestMigratedVersion = version;
    }

    if (this.latestMigratedVersion !== ACTUAL_SCHEMA_VERSION) {
      throw new MigrationError('migration missing');
    }

    return { data: this.latestMigratedData, schemaVersion: this.latestMigratedVersion };
  }
}
